"use client";

import { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { getSignedUrl } from '@/src/api/file'
import { useTranslation } from '@/src/i18n/client'

interface FilePreviewPopoverProps {
  fileId: string
  thumbnailKey: string | null
  position: { x: number; y: number }
  isVisible?: boolean
  title?: string
  metadata?: Array<{ key: string; value: string | number }>
  onClose?: () => void
}

export function FilePreviewPopover({ 
  fileId, 
  thumbnailKey, 
  position, 
  isVisible,
  title,
  metadata,
  onClose,
}: FilePreviewPopoverProps) {
  const { t } = useTranslation()
  const [shouldLoad, setShouldLoad] = useState(false)
  const visible = isVisible ?? true
  const popoverRef = useRef<HTMLDivElement>(null)

  // Only load when visible
  useEffect(() => {
    if (visible && thumbnailKey) {
      setShouldLoad(true)
    }
  }, [visible, thumbnailKey])

  // Handle click outside to close
  useEffect(() => {
    if (!visible || !onClose) return

    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    // Add a small delay to avoid closing immediately from the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [visible, onClose])

  const { data: signedUrl } = useQuery({
    queryKey: ['file', 'thumbnail', thumbnailKey],
    queryFn: async () => {
      if (!thumbnailKey) return null
      const result = await getSignedUrl({ data: { key: thumbnailKey } })
      return result?.url ?? null
    },
    enabled: shouldLoad && !!thumbnailKey,
    staleTime: 1000 * 60 * 50, // 50 min — presigned URLs expire after 1 h
    refetchInterval: (data) => {
      // If thumbnail not ready yet (null), retry every 2 seconds for up to 20 seconds
      return !data ? 2000 : false
    },
    refetchIntervalInBackground: false,
  })

  // Calculate adjusted position to keep popover on screen
  const adjustedPosition = useMemo(() => {
    if (!isVisible || typeof window === 'undefined') {
      return position
    }

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const padding = 16 // Padding from viewport edge
    
    // Popover dimensions (max-w-sm = 384px, max-h-96 = 384px + title/metadata)
    const popoverWidth = 384
    const popoverHeight = 450 // Conservative estimate with title and metadata
    
    let { x, y } = position
    
    // Check right edge - if popover would go off screen, position to the left instead
    if (x + popoverWidth + padding > viewportWidth) {
      x = Math.max(padding, viewportWidth - popoverWidth - padding)
    }
    
    // Ensure not too far left
    if (x < padding) {
      x = padding
    }
    
    // Check bottom edge
    if (y + popoverHeight + padding > viewportHeight) {
      y = Math.max(padding, viewportHeight - popoverHeight - padding)
    }
    
    // Check top edge
    if (y < padding) {
      y = padding
    }
    
    return { x, y }
  }, [position, isVisible])

  if (!visible || !signedUrl || typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div 
      ref={popoverRef}
      className="fixed z-50"
      style={{ 
        left: `${adjustedPosition.x}px`, 
        top: `${adjustedPosition.y}px` 
      }}
    >
      <div className="max-w-sm bg-background border border-border rounded-lg shadow-2xl overflow-hidden">
        <img
          src={signedUrl}
          alt="Preview"
          className="w-full max-h-96 object-contain bg-muted"
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
        
        {(title || (metadata && metadata.length > 0)) && (
          <div className="px-3 py-2 text-center border-t border-border">
            {title && (
              <div className="text-sm font-medium truncate mb-2" title={title}>
                {title}
              </div>
            )}

            {metadata && metadata.length > 0 && (
              <div className="flex flex-col gap-1.5">
                {metadata.map((item, index) => (
                  <div key={index} className="flex justify-between items-center gap-4 text-sm">
                    <span className="text-muted-foreground">{t(item.key)}</span>
                    <span className="font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
